/**
 * react-select custom styles - costeo Design System
 *
 * Why is this a JS file and not SCSS?
 * react-select only accepts styling via its `styles` prop, which must be an
 * object of functions `(provided, state) => ({...cssProperties})`. Each function
 * receives the current component state (isFocused, isSelected, isDisabled, etc.)
 * so styling can react dynamically. Plain CSS/SCSS cannot access this state.
 */

// ─── DS colour tokens ────────────────────────────────────────────────────────
const N_050  = "#f6f8fa"; // lightest neutral surface
const N_100  = "#eef2f6";
const N_200  = "#e2e8f0"; // default border
const N_300  = "#cbd5e1"; // hover border
const N_400  = "#94a3b8"; // placeholder / muted icons
const N_600  = "#475569"; // icon hover
const N_700  = "#334155"; // label / group heading text
const N_900  = "#0f172a"; // primary text

const TEAL        = "#14c3b8"; // brand-teal - focus border, selected option bg
const TEAL_050    = "#e6faf8"; // brand-teal-050 - hovered option bg, multi-value bg
const TEAL_100    = "#c8f3ef"; // brand-teal-100 - active option bg, multi-value remove hover
const TEAL_700    = "#0b8a82"; // brand-teal-700 - multi-value text
const TEAL_800    = "#054a46"; // brand-teal-800 - multi-value remove hover text

const DANGER      = "#ef4444"; // error border

// Pre-built rgba strings (for box-shadow) to avoid string repetition
const SHADOW_TEAL   = "0 0 0 3px rgba(20,195,184,.12)";
const SHADOW_DANGER = "0 0 0 3px rgba(239,68,68,.12)";
const SHADOW_MENU   = "0 4px 8px -2px rgba(15,23,42,.06), 0 2px 4px -2px rgba(15,23,42,.04)";
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the full react-select `styles` object for DropdownAtom.
 *
 * @param {object}  opts
 * @param {'small'|'medium'|'large'} opts.size
 * @param {string}  opts.error        - Truthy = red error border
 * @param {boolean} opts.disabled
 * @param {boolean} opts.isCollapsible
 */
export function getDropdownStyles({
	size = "medium",
	error = "",
	disabled = false,
	isCollapsible = false,
} = {}) {
	const sm = size === "small";
	const lg = size === "large";

	return {
		control: (provided, state) => ({
			...provided,
			minHeight: sm ? "32px" : lg ? "44px" : "36px",
			borderColor: error ? DANGER : state.isFocused ? TEAL : N_200,
			borderRadius: "7px",
			boxShadow: state.isFocused
				? error ? SHADOW_DANGER : SHADOW_TEAL
				: "none",
			"&:hover": {
				borderColor: error ? DANGER : state.isFocused ? TEAL : N_300,
			},
			backgroundColor: disabled ? N_100 : "#fff",
			transition: "border-color 0.15s, box-shadow 0.15s",
		}),

		placeholder: (provided) => ({
			...provided,
			color: N_400,
			fontSize: sm ? "12px" : "13px",
		}),

		singleValue: (provided) => ({
			...provided,
			color: N_900,
			fontSize: sm ? "12px" : "13px",
		}),

		menu: (provided) => ({
			...provided,
			zIndex: 9999,
			borderRadius: "7px",
			border: `1px solid ${N_200}`,
			boxShadow: SHADOW_MENU,
			maxHeight: isCollapsible ? "300px" : provided.maxHeight,
		}),

		menuList: (provided) => ({
			...provided,
			maxHeight: isCollapsible ? "280px" : provided.maxHeight,
			overflowY: "auto",
			padding: "4px",
		}),

		menuPortal: (provided) => ({
			...provided,
			zIndex: 9999,
		}),

		indicatorSeparator: (provided) => ({
			...provided,
			backgroundColor: N_200,
			marginTop: "8px",
			marginBottom: "8px",
		}),

		dropdownIndicator: (provided) => ({
			...provided,
			color: N_400,
			padding: sm ? "4px" : "6px",
			"&:hover": { color: N_600 },
		}),

		clearIndicator: (provided) => ({
			...provided,
			color: N_400,
			"&:hover": { color: N_600 },
		}),

		valueContainer: (provided) => ({
			...provided,
			padding: sm ? "2px 8px" : "4px 10px",
			fontSize: sm ? "12px" : "13px",
		}),

		option: (provided, state) => ({
			...provided,
			cursor: state.isDisabled ? "not-allowed" : "default",
			borderRadius: "5px",
			fontSize: sm ? "12px" : "13px",
			color: state.isDisabled ? N_400 : N_900,
			backgroundColor: state.isDisabled
				? "transparent"
				: state.isSelected
					? TEAL
					: state.isFocused
						? TEAL_050
						: "transparent",
			"&:active": {
				backgroundColor: state.isDisabled ? "transparent" : TEAL_100,
			},
			padding: sm ? "4px 10px" : "7px 10px",
		}),

		multiValue: (provided) => ({
			...provided,
			backgroundColor: TEAL_050,
			borderRadius: "5px",
		}),

		multiValueLabel: (provided) => ({
			...provided,
			color: TEAL_700,
			fontSize: "12px",
			fontWeight: 500,
		}),

		multiValueRemove: (provided) => ({
			...provided,
			color: TEAL_700,
			"&:hover": { backgroundColor: TEAL_100, color: TEAL_800 },
		}),

		groupHeading: (provided) => ({
			...provided,
			fontSize: sm ? "0.75rem" : lg ? "0.95rem" : "0.85rem",
			fontWeight: "600",
			color: N_700,
			backgroundColor: isCollapsible ? N_100 : N_050,
			padding: sm ? "6px 10px" : lg ? "10px 14px" : "8px 10px",
			margin: 0,
			borderBottom: `1px solid ${N_200}`,
			cursor: isCollapsible ? "pointer" : "default",
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			"&:hover": isCollapsible ? { backgroundColor: N_200 } : {},
		}),
	};
}

/**
 * Plain style object for the collapsible Group header rendered via customComponents.
 * Mirrors the groupHeading entry above but as a plain object (not a function),
 * since it's used as a React inline `style` prop, not via react-select's API.
 *
 * @param {object} opts
 * @param {'small'|'medium'|'large'} opts.size
 * @param {boolean} opts.isCollapsible
 */
export function getGroupHeadingStyle({
	size = "medium",
	isCollapsible = false,
} = {}) {
	const sm = size === "small";
	const lg = size === "large";
	return {
		fontSize: sm ? "0.75rem" : lg ? "0.95rem" : "0.85rem",
		fontWeight: "600",
		color: N_700,
		backgroundColor: isCollapsible ? N_100 : N_050,
		padding: sm ? "6px 10px" : lg ? "10px 14px" : "8px 10px",
		margin: 0,
		borderBottom: `1px solid ${N_200}`,
		cursor: isCollapsible ? "pointer" : "default",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	};
}
